// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 10-security-compliance.md#local-dev-cli-session
// @greenlight-spec: 16-api-conventions.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { CliSignIn } from '@greenlight/contract';

import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { LoadMore } from '@/components/greenlight/LoadMore';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { useCursorPages } from '@/lib/useCursorPages';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 20;
const QUERY_PREFIX = 'cli-sign-ins';

type Scope = 'mine' | 'org';

/**
 * Nothing on this surface says "CLI": the person recognizes the Greenlight
 * plugin in their coding assistant, not the transport underneath (docs/28
 * §`/cli/approve`).
 */
const SIGN_IN_PATH_LABEL: Record<CliSignIn['sign_in_path'], string> = {
  pairing: 'Approved with a code',
  loopback: 'Signed in with a browser',
};

const ownerName = (item: CliSignIn): string => item.user.display_name ?? item.user.email;

const absolute = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function WhenSignedIn({ item }: { item: CliSignIn }): React.JSX.Element {
  return (
    <span title={absolute(item.bound_at)} className="whitespace-nowrap">
      {relativeTime(item.bound_at)}
    </span>
  );
}

/**
 * The client label and version are chosen by an unauthenticated caller, so they
 * are shown as a record of what was claimed and labelled as such — never as
 * evidence of what actually signed in (docs/10 §Local-dev CLI session).
 */
function ReportedClient({ item }: { item: CliSignIn }): React.JSX.Element {
  const parts = [item.requester?.client_label, item.requester?.cli_version].filter(
    (part): part is string => typeof part === 'string' && part.length > 0,
  );
  if (parts.length === 0) {
    return <span className="text-[hsl(var(--text-tertiary))]">Not reported</span>;
  }
  return <span className="break-words">{parts.join(' · ')}</span>;
}

export function PluginSignIns({ userRole }: { userRole?: string }): React.JSX.Element {
  const isIT = userRole === 'org_admin' || userRole === 'admin';
  const queryClient = useQueryClient();

  const [scope, setScope] = React.useState<Scope>('mine');
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [pendingRevoke, setPendingRevoke] = React.useState<CliSignIn | null>(null);

  const queryKey = [QUERY_PREFIX, scope, cursor ?? null];
  const query = api.cli.listSignIns.useQuery(
    queryKey,
    {
      query: {
        limit: PAGE_SIZE,
        ...(scope === 'org' ? { scope } : {}),
        ...(cursor ? { cursor } : {}),
      },
    },
    { queryKey },
  );
  const pages = useCursorPages<CliSignIn>({
    query,
    cursor,
    onCursorChange: setCursor,
    getKey: (item) => item.id,
    invalidateKey: [QUERY_PREFIX, scope],
  });

  const revoke = api.cli.revokeSignIn.useMutation();

  const changeScope = (next: Scope): void => {
    if (next === scope) return;
    setScope(next);
    // A cursor is only meaningful for the scope it was issued under, so the
    // accumulated pages are dropped rather than replayed against a wider set.
    pages.reset();
  };

  const onConfirmRevoke = async (): Promise<void> => {
    const target = pendingRevoke;
    if (!target) return;
    try {
      const res = await revoke.mutateAsync({ params: { sessionId: target.id } });
      if (res.status >= 300) throw res;
      // The server's sentence is a frozen constant, identical across every
      // outcome so the response cannot be used to probe which ids exist. That
      // makes it second person, which is only true of the person who owns the
      // sign-in. In Everyone scope the reader is an admin containing someone
      // else's compromise: none of their own sessions moved, so say what the
      // confirmation they just accepted said.
      toast.success(
        scope === 'org'
          ? `That sign-in can no longer act as ${ownerName(target)}. Their local data access was signed out on every machine too.`
          : res.body.message,
      );
      setPendingRevoke(null);
      await queryClient.invalidateQueries({ queryKey: [QUERY_PREFIX, scope] });
    } catch (err) {
      toast.error(`Couldn't revoke that sign-in: ${mutationErrorMessage(err)}`);
    }
  };

  const revokeButton = (item: CliSignIn, className?: string): React.ReactNode => {
    if (item.status === 'revoked') return null;
    return (
      <Button
        variant="outline"
        size="sm"
        className={cn('h-11 md:h-9', className)}
        onClick={() => setPendingRevoke(item)}
        data-testid={`revoke-sign-in-${item.id}`}
      >
        Revoke
      </Button>
    );
  };

  const columns: DataListColumn<CliSignIn>[] = [
    ...(scope === 'org'
      ? [
          {
            key: 'user',
            header: 'Person',
            render: (item: CliSignIn) => <span className="break-words">{ownerName(item)}</span>,
          },
        ]
      : []),
    { key: 'bound_at', header: 'Signed in', render: (item) => <WhenSignedIn item={item} /> },
    {
      key: 'sign_in_path',
      header: 'How',
      priority: 'lg',
      render: (item) => SIGN_IN_PATH_LABEL[item.sign_in_path],
    },
    {
      key: 'requester',
      header: 'Client (self-reported)',
      priority: 'xl',
      render: (item) => <ReportedClient item={item} />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) =>
        item.status === 'revoked' ? (
          <span className="flex flex-col gap-0.5">
            <StatusBadge variant="revoked">Revoked</StatusBadge>
            <span className="text-xs text-[hsl(var(--text-tertiary))]">
              {item.revoked_at ? relativeTime(item.revoked_at) : null}
            </span>
          </span>
        ) : (
          <StatusBadge variant="active">Active</StatusBadge>
        ),
    },
  ];

  const renderCard = (item: CliSignIn): React.ReactNode => (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 break-words text-sm font-semibold">
          {SIGN_IN_PATH_LABEL[item.sign_in_path]}
        </span>
        {item.status === 'revoked' ? (
          <StatusBadge variant="revoked">Revoked</StatusBadge>
        ) : (
          <StatusBadge variant="active">Active</StatusBadge>
        )}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        {scope === 'org' ? (
          <>
            <dt className="text-muted-foreground">Person</dt>
            <dd className="min-w-0 break-words">{ownerName(item)}</dd>
          </>
        ) : null}
        <dt className="text-muted-foreground">Signed in</dt>
        <dd>
          <WhenSignedIn item={item} />
        </dd>
        <dt className="text-muted-foreground">Client (self-reported)</dt>
        <dd className="min-w-0">
          <ReportedClient item={item} />
        </dd>
        {item.status === 'revoked' && item.revoked_at ? (
          <>
            <dt className="text-muted-foreground">Revoked</dt>
            <dd>{relativeTime(item.revoked_at)}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );

  const emptyCopy =
    scope === 'org'
      ? {
          title: 'Nobody has signed the Greenlight plugin in yet.',
          description:
            'When someone connects the plugin in their coding assistant, their sign-in appears here and you can end it.',
        }
      : {
          title: 'The Greenlight plugin isn’t signed in anywhere.',
          description:
            'Sign in from your coding assistant and it will show up here, where you can end it at any time.',
        };

  return (
    <section className="space-y-4">
      <h2 className="text-[15px] font-semibold text-foreground">Plugin sign-ins</h2>
      {isIT && (
        <Tabs value={scope} onValueChange={(v) => changeScope(v as Scope)} activationMode="manual">
          <TabsList
            aria-label="Whose sign-ins to show"
            className="flex h-auto w-full justify-start gap-2 rounded-none border-b border-border bg-transparent p-0 text-foreground"
          >
            {(['mine', 'org'] as const).map((option) => (
              <TabsTrigger
                key={option}
                value={option}
                data-testid={`sign-ins-scope-${option}`}
                className="-mb-px h-10 shrink-0 rounded-none border-b-[3px] border-transparent px-3 text-sm font-medium text-foreground hover:border-border data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:shadow-none data-[state=active]:ring-0"
              >
                {option === 'mine' ? 'Mine' : 'Everyone'}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      <div className="space-y-4" data-testid="plugin-sign-ins">
        <p className="text-sm leading-relaxed text-muted-foreground">
          {scope === 'org'
            ? 'Every Greenlight plugin sign-in in this organization. Ending one stops it acting as that person immediately.'
            : 'Each sign-in here can act as you in Greenlight. If you don’t recognize one, end it — the plugin will ask to sign in again next time it’s used.'}
        </p>

        {/* State ladder */}
        {pages.hasError ? (
          <Alert variant="destructive" data-testid="sign-ins-error">
            <AlertTitle>Couldn&apos;t load plugin sign-ins.</AlertTitle>
            <AlertDescription>
              Something went wrong fetching the list. Retry in a moment.
            </AlertDescription>
          </Alert>
        ) : pages.isLoadingFirstPage ? (
          <div className="space-y-2" data-testid="sign-ins-loading">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="h-16 w-full rounded-gl bg-muted motion-safe:animate-pulse" />
            ))}
          </div>
        ) : pages.items.length === 0 ? (
          <EmptyState title={emptyCopy.title} description={emptyCopy.description} />
        ) : (
          <>
            <DataList
              ariaLabel="Plugin sign-ins"
              items={pages.items}
              columns={columns}
              rowKey={(item) => item.id}
              rowAction={(item) => revokeButton(item)}
              renderCard={renderCard}
              cardActions={(item) => revokeButton(item)}
              rowClassName={(item) => (item.status === 'revoked' ? 'opacity-60' : undefined)}
            />
            <LoadMore
              hasMore={pages.hasMore}
              isLoadingMore={pages.isLoadingMore}
              failed={pages.loadMoreFailed}
              onLoadMore={pages.loadMore}
              onRetry={pages.retry}
              noun="sign-ins"
              testId="sign-ins-load-more"
            />
          </>
        )}
      </div>

      <GLDrawer
        variant="confirm"
        mobileBreakpoint={768}
        open={pendingRevoke !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRevoke(null);
        }}
        preventDismiss={revoke.isPending}
        title="End this sign-in?"
        description={
          pendingRevoke
            ? `${SIGN_IN_PATH_LABEL[pendingRevoke.sign_in_path]} · ${absolute(pendingRevoke.bound_at)}`
            : undefined
        }
        contentProps={{ 'data-testid': 'revoke-sign-in-dialog' }}
        footer={
          <>
            <Button
              variant="outline"
              className="h-11 md:h-9"
              disabled={revoke.isPending}
              onClick={() => setPendingRevoke(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="h-11 md:h-9"
              disabled={revoke.isPending}
              onClick={() => void onConfirmRevoke()}
              data-testid="confirm-revoke-sign-in"
            >
              {revoke.isPending ? 'Ending…' : 'End sign-in'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm leading-relaxed">
          <p>
            The plugin using this sign-in stops acting as{' '}
            {scope === 'org' && pendingRevoke ? ownerName(pendingRevoke) : 'you'} straight away, and
            will ask to sign in again the next time it is used.
          </p>
          <p className="text-muted-foreground">
            This also signs {scope === 'org' ? 'that person' : 'you'} out of local data access on{' '}
            <strong className="font-semibold">every</strong> machine — other sign-ins stay active,
            but each reconnects the next time an app is run locally.
          </p>
        </div>
      </GLDrawer>
    </section>
  );
}
